<script setup lang="ts">
import { RefreshCw, TriangleAlert } from 'lucide-vue-next'
/**
 * "This did not load" — the sentence that replaces an empty state a request never earned.
 *
 * WHY A COMPONENT AND NOT FOUR COPIES (AlexAmin/marcus#469). Five listing paths in
 * `app/stores/documents.ts` swallowed their failures under a `// tracked by useApi` comment that was
 * false for a 500, and each one's silence surfaced as a DIFFERENT empty sentence: `common.noItems`
 * in the finder and the sidebar, `browser.groups.empty*` in the grouped list, `browser.emptyFolder`
 * under an expanded folder. They are four places, one claim, and a claim that has to read the same
 * way everywhere or a user learns to ignore it in three of them.
 *
 * `role="alert"` because this replaces a SILENCE: it has to reach a screen reader too, and on a page
 * whose whole content failed to arrive it is the one thing worth interrupting for. The `alert` role
 * is deliberate over `status` — the copy below is not progress, it is a correction of what the page
 * would otherwise have asserted.
 *
 * ALWAYS RETRYABLE, never a dead end. Every caller's failure is one request, and re-issuing it is
 * both the whole remedy and the only thing the user can do; a notice with no button would just move
 * the helplessness one step later. The retry is not debounced — it is a deliberate click on a
 * control that exists only because the last attempt failed, so there is nothing to coalesce.
 *
 * `inline` is for a notice that sits INSIDE a row's expanded children rather than owning the empty
 * area of a panel: same words, same role, sized so a nested folder does not get a full-page empty
 * state under it.
 */
withDefaults(defineProps<{
  /** i18n-resolved sentence about the ATTEMPT ("we could not load…"), never about the content. */
  message: string
  /** The second line that says what the failure is NOT. Omitted in `inline`. */
  hint?: string | null
  variant?: 'block' | 'inline'
}>(), { hint: null, variant: 'block' })

const emit = defineEmits<{ retry: [] }>()

const { t } = useI18n()
</script>

<template>
  <div
    v-if="variant === 'inline'"
    role="alert"
    class="flex items-center gap-2 px-3 py-2 text-[10px] text-destructive"
  >
    <TriangleAlert class="h-3 w-3 shrink-0" />
    <span>{{ message }}</span>
    <button
      type="button"
      class="underline underline-offset-2 hover:no-underline"
      @click="emit('retry')"
    >
      {{ t('common.retry') }}
    </button>
  </div>

  <div v-else role="alert" class="px-4 py-12 text-center">
    <p class="text-sm font-medium text-foreground">{{ message }}</p>
    <p v-if="hint" class="mt-1 text-xs text-muted-foreground">{{ hint }}</p>
    <Button variant="outline" size="sm" class="mt-3" @click="emit('retry')">
      <RefreshCw class="mr-1.5 h-3.5 w-3.5" />{{ t('common.retry') }}
    </Button>
  </div>
</template>
